'use client';

import { useCallback } from 'react';
import config from 'config';
import { useSetSecureMessageArchivedMutation } from 'services/userProfileApi';

import {
  getSecureMessageRowIds,
  getSecureMessageThreadId,
  isSecureMessageArchived
} from 'utils/helpers/secureMessages';

import { SecureMessageListItem } from 'types';

const { secureMessages } = config;

export const getSecureMessageArchiveLabel = (
  item: SecureMessageListItem
): string =>
  isSecureMessageArchived(item)
    ? secureMessages?.unarchiveLabel ?? 'Unarchive'
    : secureMessages?.archiveLabel ?? 'Archive';

interface UseSecureMessageArchiveOptions {
  /** Fired once the row changed state, so a list can drop it without waiting for a refetch. */
  onArchivedChange?: (threadId: number) => void;
}

/**
 * Archives or restores a whole thread. Which way it goes is read from the row itself,
 * so the same menu entry serves the active and the archived list.
 */
export const useSecureMessageArchive = ({
  onArchivedChange
}: UseSecureMessageArchiveOptions = {}) => {
  const [setSecureMessageArchived, { isLoading }] =
    useSetSecureMessageArchivedMutation();

  const toggleArchived = useCallback(
    async (item: SecureMessageListItem) => {
      if (isLoading) {
        return;
      }

      try {
        await setSecureMessageArchived({
          msgIds: getSecureMessageRowIds(item),
          isArchived: !isSecureMessageArchived(item)
        }).unwrap();

        onArchivedChange?.(getSecureMessageThreadId(item));
      } catch (error) {
        console.error(
          `Error while changing secure message archive state: ${error}`
        );
      }
    },
    [isLoading, onArchivedChange, setSecureMessageArchived]
  );

  return {
    isArchiveAllow: secureMessages?.isArchiveAvailable ?? false,
    isArchiving: isLoading,
    toggleArchived
  };
};
